import { EsgTermsResponse } from '@/research/researchQueries'
import { analytics } from '@/common/analytics/analytics'
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/common/ui/tooltip'
import React from 'react'
import IntercomVisibilityController from '@/common/ui/IntercomVisibilityController'
import { Button } from '../ui/button'
import { XIcon } from 'lucide-react'
import { useIsSmallScreen } from '../ui/useIsSmallScreen'

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')

export function createContentfulRenderText(
    meanings: EsgTermsResponse,
    caseSensitive = false,
): (text: string) => React.ReactNode {
    const meaningsMap: Record<string, string> = {}
    meanings.forEach((m) => {
        if (m?.word) meaningsMap[m.word] = m.definition ?? ''
    })

    const keys = Object.keys(meaningsMap)
        .filter(Boolean)
        .sort((a, b) => b.length - a.length)
    if (keys.length === 0) return (t: string) => t

    const pattern = keys.map(escapeRegExp).join('|')
    const flags = caseSensitive ? 'g' : 'gi'
    const regex = new RegExp(`\\b(${pattern})\\b`, flags)

    return (text: string) => {
        const parts: React.ReactNode[] = []
        let lastIndex = 0
        let match: RegExpExecArray | null
        let uid = 0

        // TermTooltip component now calls useIsSmallScreen inside
        const TermTooltip: React.FC<{
            tid: string
            termText: string
            termKey: string
            content: React.ReactNode
        }> = ({ tid, termText, termKey, content }) => {
            const [open, setOpen] = React.useState(false)
            const isSmallScreen = useIsSmallScreen()

            const handleOpenChange = (next: boolean) => {
                if (next) {
                    analytics.trackWithDefaultsDebounced('definition_viewed', termKey, { term: termKey })
                }
                setOpen(next)
            }

            return isSmallScreen ? (
                <>
                    <IntercomVisibilityController open={open} />
                    <span className="relative inline-block">
                        <button
                            type="button"
                            onClick={() => handleOpenChange(!open)}
                            className="relative inline-block group cursor-help focus:outline-none underline decoration-transparent after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-full after:bg-brand-gradient"
                            aria-describedby={tid}
                        >
                            {termText}
                        </button>

                        {open && (
                            <>
                                <div
                                    className="fixed inset-0 z-[99990] bg-black/40"
                                    aria-hidden="true"
                                    onClick={() => setOpen(false)}
                                />
                                <div
                                    id={tid}
                                    role="dialog"
                                    aria-modal="true"
                                    className="fixed left-0 right-0 bottom-0 z-[99999] bg-white rounded-t-2xl shadow-lg p-4 max-h-[60vh] overflow-auto after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-[4px] after:w-full after:bg-brand-gradient"
                                >
                                    <div className="w-full flex justify-end">
                                        <Button
                                            variant={'tertiary'}
                                            size={'icon'}
                                            className="w-8 h-8 p-0"
                                            onClick={() => setOpen(false)}
                                            aria-label={`Close ${termKey} tooltip`}
                                        >
                                            <XIcon className="w-4 h-4 text-black" />
                                        </Button>
                                    </div>
                                    <div className="mt-2">
                                        <span className="text-black text-base font-semibold leading-6">{termKey}</span>
                                        <div className="mt-2 text-black text-base font-normal leading-6">{content}</div>
                                    </div>
                                </div>
                            </>
                        )}
                    </span>
                </>
            ) : (
                <TooltipProvider>
                    <Tooltip open={open} onOpenChange={handleOpenChange}>
                        <TooltipTrigger asChild>
                            <span
                                className="relative inline-block group cursor-help focus:outline-none underline decoration-transparent after:content-[''] after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-full after:bg-brand-gradient"
                                style={
                                    open
                                        ? {
                                              background:
                                                  'linear-gradient(107deg, rgba(212, 213, 229, 0.20) 15.25%, rgba(207, 236, 122, 0.20) 65.6%, rgba(202, 143, 77, 0.20) 113.88%)',
                                          }
                                        : undefined
                                }
                                tabIndex={0}
                                aria-describedby={tid}
                                onClick={() => handleOpenChange(true)}
                            >
                                {termText}
                            </span>
                        </TooltipTrigger>
                        <TooltipContent
                            id={tid}
                            side="bottom"
                            align="start"
                            hideArrow
                            className="md:min-w-[20rem] sm:max-w-[28rem] p-4 rounded-lg shadow z-[99999] duration-100 border-2 border-transparent"
                            style={{
                                background:
                                    'linear-gradient(#FFFFFF, #FFFFFF) padding-box, var(--brand-gradient) border-box',
                            }}
                        >
                            <span className="text-black text-base font-semibold leading-6">{termKey}</span>
                            <div className="mt-2 text-black text-base font-normal leading-6">{content}</div>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )
        }

        while ((match = regex.exec(text))) {
            const idx = match.index
            const matched = match[0]
            if (idx > lastIndex) parts.push(text.slice(lastIndex, idx))

            const key = keys.find((k) =>
                caseSensitive ? k === matched : k.toLowerCase() === matched.toLowerCase(),
            ) as string

            const id = `hm-${uid++}-${idx}`

            parts.push(<TermTooltip key={id} tid={id} termText={matched} termKey={key} content={meaningsMap[key]} />)

            lastIndex = idx + matched.length
        }

        if (lastIndex < text.length) parts.push(text.slice(lastIndex))
        return <>{parts}</>
    }
}

export default createContentfulRenderText
